import React, { useEffect, useState } from "react";
import { vendorProducts, vendorDeleteProduct, vendorToggleProductStock } from "@/lib/vendorApi";
import { resolveImageUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Plus, Trash2, Package, Edit3 } from "lucide-react";
import ProductSearchBar, { filterProducts } from "@/components/vendor/ProductSearchBar";

export default function MyProducts({ shops, onAdd, onEdit, refreshKey, onChanged }) {
  const { formatApiError } = useApp();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const list = await vendorProducts();
      setProducts(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [refreshKey]);

  const del = async (id) => {
    if (!confirm("Delete this product?")) return;
    try {
      await vendorDeleteProduct(id);
      toast.success("Deleted");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleStock = async (p) => {
    const current = p.in_stock !== false;
    const next = !current;
    // Optimistic update
    setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, in_stock: next } : x)));
    setTogglingId(p.id);
    try {
      await vendorToggleProductStock(p.id, next);
      toast.success(next ? "Marked available" : "Marked out of stock");
      onChanged?.();
    } catch (e) {
      // Rollback
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, in_stock: current } : x)));
      toast.error(formatApiError(e));
    } finally {
      setTogglingId(null);
    }
  };

  const { list: shown, termFilter } = filterProducts(products, query, filter);
  const shopName = (sid) => shops.find((s) => s.shop_id === sid)?.name || sid;

  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-4 sm:p-6 min-w-0" data-testid="my-products">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="min-w-0">
          <h2 className="font-display text-xl font-bold">My Products ({products.length})</h2>
          <p className="text-sm text-slate-500">Manage products across your shops.</p>
        </div>
        <button onClick={onAdd} className="inline-flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-3 sm:px-4 py-2 text-sm font-semibold flex-shrink-0" data-testid="add-product-btn">
          <Plus className="w-4 h-4" /> <span className="hidden xs:inline">Add product</span><span className="xs:hidden">Add</span>
        </button>
      </div>
      {loading ? (
        <div className="py-12 text-center text-slate-500 text-sm">Loading...</div>
      ) : products.length === 0 ? (
        <div className="py-12 text-center">
          <Package className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <div className="font-display text-lg font-bold">No products yet</div>
          <p className="text-sm text-slate-500 mt-1">Add your first product to start selling.</p>
        </div>
      ) : (
        <>
        <ProductSearchBar query={query} setQuery={setQuery} filter={filter} setFilter={setFilter} shown={shown.length} total={products.length} termFilter={termFilter} />
        {shown.length === 0 && (
          <div className="py-10 text-center" data-testid="product-search-empty">
            <div className="font-display text-base font-bold text-slate-800">No matching products found.</div>
            <p className="text-sm text-slate-500 mt-1">Try a different name or filter.</p>
            <button type="button" onClick={() => { setQuery(""); setFilter("all"); }} className="mt-3 inline-flex rounded-full border border-slate-200 hover:border-emerald-500 px-3 py-1.5 text-xs font-semibold text-slate-700" data-testid="product-search-reset">Show all products</button>
          </div>
        )}
        <div className="space-y-3">
          {shown.map((p) => {
            const inStock = p.in_stock !== false;
            const busy = togglingId === p.id;
            return (
              <div key={p.id} className={`border rounded-xl p-3 ${inStock ? "border-slate-100" : "border-slate-200 bg-slate-50"} min-w-0`} data-testid={`vendor-product-${p.id}`}>
                {/* Row 1: image + core info (always visible) */}
                <div className="flex items-start gap-3 min-w-0">
                  <img src={resolveImageUrl(p.image)} alt="" className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2 flex-wrap">
                      <div className="text-sm font-semibold text-slate-900 line-clamp-2 min-w-0 break-words">{p.name}</div>
                      {!inStock && <span className="text-[10px] uppercase font-bold bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded-full flex-shrink-0">Out of stock</span>}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{shopName(p.shop_id)}</div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-slate-600 flex-wrap">
                      <span className="font-bold text-emerald-800">₹{p.price}</span>
                      {p.mrp > p.price && <span className="text-slate-400 line-through">₹{p.mrp}</span>}
                      {p.unit && <span className="text-slate-400">· {p.unit}</span>}
                    </div>
                  </div>
                </div>
                {/* Row 2: action bar — wraps naturally; stays inside the card at any width */}
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Availability</span>
                    <button
                      onClick={() => toggleStock(p)}
                      disabled={busy}
                      className={`relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none disabled:opacity-60 ${inStock ? "bg-emerald-600" : "bg-slate-300"}`}
                      aria-label="Toggle availability"
                      aria-pressed={inStock}
                      data-testid={`stock-toggle-${p.id}`}
                      title={inStock ? "Available — tap to mark out of stock" : "Out of stock — tap to mark available"}
                    >
                      <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition ${inStock ? "translate-x-5" : "translate-x-0"}`} />
                    </button>
                    <span className={`text-xs font-semibold ${inStock ? "text-emerald-700" : "text-slate-500"}`}>{inStock ? "Available" : "Unavailable"}</span>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
                    <button onClick={() => onEdit?.(p)} className="inline-flex items-center gap-1 rounded-full border border-slate-200 hover:border-emerald-500 hover:text-emerald-700 px-2.5 py-1.5 text-xs font-semibold text-slate-600" aria-label="Edit" data-testid={`edit-${p.id}`}>
                      <Edit3 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Edit</span>
                    </button>
                    <button onClick={() => del(p.id)} className="inline-flex items-center gap-1 rounded-full border border-slate-200 hover:border-red-300 hover:text-red-600 px-2.5 py-1.5 text-xs font-semibold text-slate-500" aria-label="Delete" data-testid={`delete-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}
    </div>
  );
}
